import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import LanguageSelector from '../../components/LanguageSelector';
import OTPInput from '../../components/Auth/OTPInput';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';

const RegisterVerifyOtp: React.FC = () => {
  const { t } = useTranslation('register');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuthStatus } = useAuth();
  const email = searchParams.get('email') || '';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const startResendCooldown = () => {
    setResendCooldown(30);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const onSubmitOTP = async (enteredOtp: string) => {
    if (!email) {
      toast.error(t('steps.otp.emailMissing'));
      return;
    }
    try {
      setIsSubmitting(true);
      setOtpError('');
      await apiService.verifyEmail(email, enteredOtp);
      await checkAuthStatus();
      toast.success(t('messages.verified') || 'Email verified. Welcome!');
      navigate('/');
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || t('messages.failed');
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const doResend = async () => {
    if (resendCooldown > 0) return;
    try {
      setIsSubmitting(true);
      setOtpError('');
      await apiService.resendVerificationOtp(email);
      toast.success(t('messages.checkEmail'));
      startResendCooldown();
    } catch (e: any) {
      toast.error(e?.message || t('messages.failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12 relative">
        <div className="absolute top-4 right-4 z-10">
          <LanguageSelector />
        </div>
        <div className="text-center text-white">
          <p className="mb-4">{t('steps.otp.missingEmail')}</p>
          <Link to="/register" className="text-white underline">{t('steps.otp.backToRegister')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12 relative">
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>
      <div className="w-full max-w-md">
        <Link
          to="/register"
          className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {t('back')}
        </Link>
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 md:p-8 shadow-2xl">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-white mb-2">{t('steps.otp.title')}</h2>
            <p className="text-gray-400 text-sm">
              {t('steps.otp.description')} <span className="font-semibold text-white">{email}</span>
            </p>
          </div>
          <div className="space-y-4">
            <OTPInput
              onComplete={onSubmitOTP}
              disabled={isSubmitting}
              error={otpError}
            />
            <div className="flex justify-center">
              <button
                type="button"
                onClick={doResend}
                disabled={isSubmitting || resendCooldown > 0}
                className="text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendCooldown > 0 ? t('steps.otp.resendIn', { count: resendCooldown }) : t('steps.otp.resend')}
              </button>
            </div>
          </div>
        </div>
        <p className="text-center text-sm text-gray-600 mt-6">
          {t('footer')}{' '}
          <Link to="/login" className="text-white hover:underline font-medium">{t('loginLink')}</Link>
        </p>
        <p className="text-center text-sm text-gray-600 mt-2">{t('copyright')}</p>
      </div>
    </div>
  );
};

export default RegisterVerifyOtp;
